import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { authApi } from 'api/auth';
import { setAccessToken } from 'api/client';

export default function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData?.email) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) errs.email = 'Enter a valid email address';
    if (!formData?.password) errs.password = 'Password is required';
    else if (formData?.password?.length < 6) errs.password = 'Password must be at least 6 characters';
    return errs;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitError('');
    setLoading(true);

    try {
      const response = await authApi.login({
        email: formData?.email,
        password: formData?.password,
      });

      if (response?.token) {
        setAccessToken(response.token);
      }

      setLoading(false);
      const from = location?.state?.from?.pathname;
      navigate(from && from !== '/login' ? from : '/today');
    } catch (error) {
      setLoading(false);
      setSubmitError(error.message || 'Login failed. Please try again.');
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
      {/* Email */}
      <div className="flex flex-col gap-1.5">
        <label className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
          Email Address
        </label>
        <div className="relative">
          <Icon
            name="Mail"
            size={16}
            color={errors?.email ? 'var(--color-error)' : 'var(--color-text-secondary)'}
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border font-caption text-sm transition-all duration-200"
            style={{
              borderColor: errors?.email ? 'var(--color-error)' : 'var(--color-border)',
              background: 'var(--color-background)',
              color: 'var(--color-foreground)',
              outline: 'none',
            }}
            onFocus={(e) => { if (!errors?.email) e.target.style.borderColor = 'var(--color-primary)'; }}
            onBlur={(e) => { if (!errors?.email) e.target.style.borderColor = 'var(--color-border)'; }}
          />
          {formData?.email && !errors?.email && (
            <Icon name="CheckCircle" size={16} color="var(--color-success)" className="absolute right-3 top-1/2 -translate-y-1/2" />
          )}
        </div>
        {errors?.email && (
          <div className="flex items-center gap-1.5">
            <Icon name="AlertCircle" size={12} color="var(--color-error)" />
            <span className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>{errors?.email}</span>
          </div>
        )}
      </div>
      {/* Password */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
            Password
          </label>
          <span className="font-caption text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
            Contact your admin to reset your password
          </span>
        </div>
        <div className="relative">
          <Icon
            name="Lock"
            size={16}
            color={errors?.password ? 'var(--color-error)' : 'var(--color-text-secondary)'}
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={(e) => handleChange('password', e?.target?.value)}
            className="w-full pl-10 pr-12 py-3 rounded-xl border font-caption text-sm transition-all duration-200"
            style={{
              borderColor: errors?.password ? 'var(--color-error)' : 'var(--color-border)',
              background: 'var(--color-background)',
              color: 'var(--color-foreground)',
              outline: 'none',
            }}
            onFocus={(e) => { if (!errors?.password) e.target.style.borderColor = 'var(--color-primary)'; }}
            onBlur={(e) => { if (!errors?.password) e.target.style.borderColor = 'var(--color-border)'; }}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            onClick={() => setShowPassword((p) => !p)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" />
          </button>
        </div>
        {errors?.password && (
          <div className="flex items-center gap-1.5">
            <Icon name="AlertCircle" size={12} color="var(--color-error)" />
            <span className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>{errors?.password}</span>
          </div>
        )}
      </div>
      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-caption font-semibold text-sm transition-all duration-200"
        style={{
          background: loading ? 'var(--color-muted)' : 'var(--color-primary)',
          color: loading ? 'var(--color-text-secondary)' : '#FFFFFF',
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
        onMouseEnter={(e) => { if (!loading) e.currentTarget.style.opacity = '0.9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        {loading ? (
          <>
            <div
              className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: 'var(--color-text-secondary)', borderTopColor: 'transparent' }}
            />
            Signing in...
          </>
        ) : (
          <>
            <Icon name="LogIn" size={16} color="#FFFFFF" />
            Sign In to CRMAnalytics
          </>
        )}
      </button>
      {submitError && (
        <div className="flex items-center gap-1.5">
          <Icon name="AlertTriangle" size={12} color="var(--color-error)" />
          <span className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>{submitError}</span>
        </div>
      )}
      <p className="font-caption text-xs text-center" style={{ color: 'var(--color-text-secondary)' }}>
        Single sign-on is managed by your workspace admin
      </p>
    </form>
  );
}
